/**
 * Webflow Platform Utility
 *
 * Handles Webflow-specific initialization requirements including IX3 detection.
 * Auto-detects Webflow via data-wf-* attributes on <html> element.
 *
 * Features:
 * - Auto-detects Webflow platform
 * - Waits for IX3 initialization
 * - Manual override with data-hs-wf-off
 */

/**
 * Detect if running on Webflow platform
 * Checks for any data-wf-* attribute on <html> element
 */
export function isWebflow(): boolean {
  const html = document.documentElement;
  const attributes = html.getAttributeNames();
  return attributes.some((attr) => attr.startsWith('data-wf-'));
}

/**
 * Wait for Webflow IX3 to be ready
 * Polls for multiple IX ready signals indefinitely (no timeout)
 * @returns Promise<void> - resolves when IX3 is detected
 */
export async function waitForIx3(): Promise<void> {
  return new Promise((resolve) => {
    const checkIx3 = () => {
      const html = document.documentElement;

      // Check all known IX ready signals (supports old and new Webflow versions)
      const hasLegacyIx3 = html.classList.contains('w-mod-ix3');
      const hasNewIxSignal = html.hasAttribute('data-wf-ix-ready');
      const hasAnyIxSignal = hasLegacyIx3 || hasNewIxSignal;

      if (hasAnyIxSignal) {
        resolve(); // IX3 loaded successfully
      } else {
        setTimeout(checkIx3, 50); // Keep checking indefinitely
      }
    };
    checkIx3();
  });
}

/**
 * Check if Webflow waiting should be disabled via script attribute
 * Looks for data-hs-wf-off on module script tag
 */
export function shouldSkipWebflow(scripts: Element[]): boolean {
  return scripts.some((script) => script.hasAttribute('data-hs-wf-off'));
}
